import { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import Icons from "../../assets/index"

interface NamingGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
}

const styles = {
  dialogPaper: {
    borderRadius: "16px",
    padding: "24px",
    margin: "16px",
    maxWidth: "400px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "4.71vw",
    fontWeight: 700,
    color: "#191919",
    flex: 1,
    textAlign: "center" as const,
  },
  closeBtn: {
    padding: 0,
    color: "#191919",
  },
  instructionText: {
    fontSize: "3.5vw",
    color: "#333",
    lineHeight: 1.5,
    marginBottom: "12px",
  },
  imageCard: {
    position: "relative" as const,
    width: "100%",
    height: "140px",
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "24px",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  imageOverlay: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: "2%",
    background: "#00000066",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  overlayText: {
    color: "#fff",
    fontSize: "3.5vw",
    fontWeight: 500,
  },
  pinIcon: {
    color: "#fff",
    fontSize: "4.5vw",
  },
  checkboxContainer: {
    marginBottom: "16px",
  },
  checkboxLabel: {
    fontSize: "3.5vw",
    color: "#666",
  },
  gotItBtn: {
    width: "100%",
    padding: "12px",
    borderRadius: "30px",
    backgroundColor: "#9443DF",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "4vw",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#7c37bb",
      boxShadow: "none",
    },
  },
};

export default function NamingGuidelinesModal({
  open,
  onClose,
}: NamingGuidelinesModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleGotIt = () => {
    if (dontShowAgain) {
      localStorage.setItem("hideNamingGuidelines", "true");
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: styles.dialogPaper }}>
      <Box sx={styles.header}>
        <Box sx={{ width: 24 }} /> {/* Spacer to center title */}
        <Typography sx={styles.title}>Naming Guidelines</Typography>
        <IconButton onClick={onClose} sx={styles.closeBtn}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography sx={styles.instructionText}>
        If the building has an <b>official name</b>, use that name.
      </Typography>

      <Box sx={styles.imageCard}>
        <img src={Icons.PalladiumNameGuide} alt="Palladium" style={styles.image} />
        <Box sx={styles.imageOverlay}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={styles.pinIcon}
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <Typography sx={styles.overlayText}>Palladium</Typography>
        </Box>
      </Box>

      <Typography sx={styles.instructionText}>
        If there is no name, describe the building using a <b>nearby place or landmark.</b>
      </Typography>

      <Box sx={styles.imageCard}>
        <img src={Icons.BuildingNameGuide} alt="Building Near Parking Space" style={styles.image} />
        <Box sx={styles.imageOverlay}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={styles.pinIcon}
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <Typography sx={styles.overlayText}>Building Near Parking Space</Typography>
        </Box>
      </Box>

      <Box sx={styles.checkboxContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              sx={{ color: "#9443DF", "&.Mui-checked": { color: "#9443DF" } }}
              size="small"
            />
          }
          label={<Typography sx={styles.checkboxLabel}>Don't show again</Typography>}
        />
      </Box>

      <Button variant="contained" onClick={handleGotIt} sx={styles.gotItBtn}>
        Got it
      </Button>
    </Dialog>
  );
}
